<template>
  <div>
    <app-loader v-if="loading"></app-loader>
    <v-container class="pa-8 mt-8" v-if="evaluacion.pregunta">
    <v-row>
      <v-col cols="6" sm="8" class="pb-1">
        <div class="d-inline-flex align-start">
          <v-avatar color="primary" class="rounded-lg" size="48">
            <v-icon color="white">mdi-file-document</v-icon>
          </v-avatar>
          <div class="d-flex flex-column align-start px-2">
              <h5 style="color: #727588;">Cuestionario {{ tipoEvaluacion(evaluacion.id_tipo_evaluacion) }}</h5>
              <h3 class="primary--text font-weight-bold mr-3">
                {{ evaluacion.nombre_evaluacion }}
              </h3>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="4" class="d-flex justify-end align-start">
        <div class="timer-badge d-inline-flex align-center font-weight-bold">
          <v-icon color="#cf7a7c" small class="mr-1">mdi-timer-outline</v-icon>
          {{ timer }} restantes
        </div>
      </v-col>
    </v-row>

    <v-row v-if="mostrarRevision" class="pt-0 mt-0">
      <v-col cols="12">
        <div class="d-flex justify-end mb-1">
          <span class="secondaryGray--text text-caption">
            Pregunta {{ evaluacion.numero }} de {{ evaluacion.total }}
          </span>
        </div>
      </v-col>
    </v-row>

    <template v-if="!mostrarRevision">
      <v-row>
        <v-col cols="12">
          <BarraProgreso
            :pregunta-actual="evaluacion.numero"
            :total-preguntas="evaluacion.total"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card rounded="lg" outlined class="pa-6">
            <div class="d-flex justify-start align-center mb-4">
              <span class="rounded-lg px-3 text-subtitle-2 font-weight-bold" style="border: 1px #e2e7ff; background-color: #e2e7ff;color: #1c1e4d;">
                Pregunta {{ evaluacion.numero }}
              </span>
            </div>

            <div class="primary--text font-weight-bold text-h6 mb-6">
              {{ evaluacion.pregunta.pregunta }}
            </div>

            <v-img
              v-if="evaluacion.pregunta.path_img"
              :src="evaluacion.pregunta.path_img"
              max-height="300"
              contain
              class="mb-6"
            ></v-img>

            <!-- Verdadero / Falso -->
            <div
              v-if="evaluacion.pregunta.id_tipo_pregunta === 3"
              class="d-flex flex-column"
            >
              <v-card
                v-for="(opcion, idx) in evaluacion.pregunta.opciones"
                :key="opcion.id"
                outlined
                rounded="lg"
                class="opcion-card d-flex align-center mr-4 mb-3 px-4 py-3"
                :class="{ 'opcion-card--selected': respuestas[evaluacion.pregunta.id] === opcion.id }"
                @click="seleccionarOpcion(opcion)"
              >
                <div
                  class="opcion-badge d-flex align-center justify-center font-weight-bold mr-3"
                  :class="{ 'opcion-badge--selected': respuestas[evaluacion.pregunta.id] === opcion.id }"
                >
                  {{ letraOpcion(idx) }}
                </div>
                <span>{{ opcion.opcion }}</span>
              </v-card>
            </div>

            <!-- Opción múltiple -->
            <div v-else-if="evaluacion.pregunta.id_tipo_pregunta === 2">
              <v-card
                v-for="(opcion, idx) in evaluacion.pregunta.opciones"
                :key="opcion.id"
                outlined
                rounded="lg"
                class="opcion-card d-flex align-center mb-3 px-4 py-3"
                :class="{ 'opcion-card--selected': respuestas[evaluacion.pregunta.id] === opcion.id }"
                @click="seleccionarOpcion(opcion)"
              >
                <div
                  class="opcion-badge d-flex align-center justify-center font-weight-bold mr-3"
                  :class="{ 'opcion-badge--selected': respuestas[evaluacion.pregunta.id] === opcion.id }"
                >
                  {{ letraOpcion(idx) }}
                </div>
                <span>{{ opcion.opcion }}</span>
              </v-card>
            </div>

            <!-- Pregunta abierta -->
            <v-textarea
              v-else
              v-model="respuestas[evaluacion.pregunta.id]"
              outlined
              class="rounded-lg"
              label="Tu respuesta"
              color="primary"
              rows="4"
            ></v-textarea>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="d-flex justify-space-between">
          <v-btn
            outlined
            rounded
            color="primary"
            class="text-none"
            :disabled="evaluacion.numero === 1"
            @click="anterior"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Anterior
          </v-btn>

          <div>
            <v-btn
              text
              color="primary"
              class="text-none"
              @click="omitir"
            >
              Omitir por ahora
            </v-btn>
            <v-btn
              color="primary"
              dark
              rounded
              class="text-none"
              @click="siguiente"
            >
              Siguiente
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <v-row>
        <v-col cols="12">
          <v-card rounded="lg" outlined flat color="help-cards-color" class="pa-4">
            <div class="d-flex">
              <v-icon color="primary" class="mr-2">mdi-information</v-icon>
              <div>
                <div class="primary--text font-weight-bold">
                  Instrucciones de revisión
                </div>
                <div class="secondaryGray--text">
                  Haga clic en cualquier número de pregunta para volver y modificar su
                  respuesta. Asegúrese de que todas las preguntas marcadas para revisión
                  hayan sido validadas antes de finalizar.
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-divider class="revision-divider"></v-divider>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-for="(pregunta, index) in resumen.preguntas"
          :key="index"
          cols="6"
          sm="4"
          md="3"
          lg="2"
          class="d-flex"
        >
          <v-card
            rounded="lg"
            outlined
            :class="{ 'pregunta-pendiente': !estaContestada(pregunta) }"
            class="pa-4 flex-grow-1 revision-card"
            @click="irAPregunta(pregunta.numero)"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="primary--text font-weight-bold text-caption">
                  Pregunta {{ String(index + 1).padStart(2, '0') }}
                </div>
                <div
                  class="text-caption"
                  :class="estaContestada(pregunta) ? 'success--text' : 'secondaryGray--text'"
                >
                  {{ estaContestada(pregunta) ? 'Completada' : 'Pendiente' }}
                </div>
              </div>
              <v-icon v-if="estaContestada(pregunta)" color="success">
                mdi-check-circle
              </v-icon>
              <v-icon v-else color="secondaryGray">
                mdi-circle-outline
              </v-icon>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row justify="center" class="mt-4">
        <v-col cols="6" sm="3" md="2">
          <v-card rounded="lg" outlined class="pa-4 text-center">
            <div class="primary--text font-weight-bold text-h5">
              {{ resumen.contestadas }}
            </div>
            <div class="secondaryGray--text text-caption text-uppercase">
              Contestadas
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-card rounded="lg" outlined class="pa-4 text-center">
            <div class="primary--text font-weight-bold text-h5">
              {{ resumen.omitidas }}
            </div>
            <div class="secondaryGray--text text-caption text-uppercase">
              Sin responder
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-divider class="my-4"></v-divider>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="d-flex justify-space-between">
          <v-btn
            outlined
            rounded
            color="primary"
            class="text-none"
            @click="mostrarRevision = false"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Anterior
          </v-btn>
          <v-btn
            color="primary"
            dark
            rounded
            class="text-none"
            @click="openModalFinalizar()"
          >
            Finalizar evaluación
          </v-btn>
        </v-col>
      </v-row>
      <v-dialog v-model="modalFinalizar" max-width="500" persistent>
        <v-card rounded="lg" class="pa-8 text-center">
          <div class="text-h6 font-weight-bold primary--text mb-2">
            Confirmar finalización
          </div>
          <div class="secondaryGray--text mb-6">
            Estás a punto de finalizar tu evaluación<br />
            Verifica que hayas respondido todas las preguntas, ya que una vez
            enviada no podrás modificar tus respuestas.
          </div>
          <div class="d-flex justify-center">
            <v-btn
              outlined
              rounded
              color="primary"
              class="text-none mr-4"
              @click="modalFinalizar = false"
            >
              Revisar respuestas
            </v-btn>
            <v-btn
              color="primary"
              dark
              rounded
              class="text-none"
              :loading="loading"
              @click="confirmarFinalizar"
            >
              Finalizar evaluación
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="modalEvaluacionFinalizada" max-width="500" persistent>
        <v-card rounded="lg" class="pa-8 text-center">
          <div class="text-h6 font-weight-bold primary--text mb-2">
            Evaluación completada
          </div>
          <div class="secondaryGray--text mb-6">
            Tu evaluación se ha enviado exitosamente<br />
            Gracias por completar esta etapa del proceso de selección
          </div>
          <v-btn
            color="primary"
            dark
            rounded
            class="text-none"
            @click="aceptarFinalizacion"
          >
            Aceptar
          </v-btn>
        </v-card>
      </v-dialog>
    </template>
    </v-container>
  </div>
</template>

<script>
import BarraProgreso from './components/BarraProgreso.vue';
import evaluacionContratacion from '@/services/evaluacionContratacion.services';
import AppLoader from '@/components/AppLoader.vue'

export default {
  name: 'EvaluacionEjecucion',
  components: {
    BarraProgreso,
    AppLoader
  },
  data() {
    return {
      evaluacion: {},
      respuestas: {},
      mostrarRevision: false,
      segundosRestantes: 45 * 60,
      timerInterval: null,
      loading: false,
      resumen: {},
      modalFinalizar: false,
      modalEvaluacionFinalizada: false,
      idOferta: null,
    };
  },
  computed: {
    timer() {
      const horas = Math.floor(this.segundosRestantes / 3600);
      const minutos = Math.floor((this.segundosRestantes % 3600) / 60);
      const segundos = this.segundosRestantes % 60;
      return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:${String(segundos).padStart(2, '0')}`;
    },
  },
  methods: {
    async getEvaluacion() {
      this.loading = true
      try {
        const response = await evaluacionContratacion.startExam(this.$route.params.idEvaluacion);
        this.evaluacion = response.data.data;
        this.temporalAlert({
          show: true,
          type: 'success',
          message: response.data.message
        })
        this.cargarRespuestaPregunta();
        this.segundosRestantes = (this.evaluacion.hora_duracion_eval || 0) * 3600 + (this.evaluacion.minuto_duracion_eval || 0) * 60;
        this.iniciarTimer();
        this.loading = false
      } catch (e) {
        this.$router.push({name: 'evaluacion-instrucciones', params: { idEvaluacion: this.$route.params.idEvaluacion}});
        this.loading = false
      }
    },
    tipoEvaluacion(tipo) {
      switch (tipo) {
        case 1: 
          return 'Entrevista';
          break;
        case 2: 
          return 'Técnica';
          break;
        case 3: 
          return 'Psicométrica';
          break;
        default:
          break;
      }
    },
    estaContestada(pregunta) {
      return pregunta.contestada === true;
    },
    cargarRespuestaPregunta() {
      const pregunta = this.evaluacion.pregunta;
      if (!pregunta) return;
      const respuestaGuardada = Array.isArray(pregunta.respuestas) ? pregunta.respuestas[0] : pregunta.respuestas;
      if (respuestaGuardada !== undefined && respuestaGuardada !== null && respuestaGuardada !== '') {
        this.$set(this.respuestas, pregunta.id, respuestaGuardada);
      }
    },
    letraOpcion(index) {
      return String.fromCharCode(65 + index);
    },
    seleccionarOpcion(opcion) {
      this.$set(this.respuestas, this.evaluacion.pregunta.id, opcion.id);
    },
    async anterior() {
      if(this.evaluacion.pregunta_anterior !== null) {
        const response = await evaluacionContratacion.getObtenerPregunta(this.evaluacion.uuid_evaluacion_respuesta, this.evaluacion.pregunta_anterior)
        this.evaluacion = response.data.data;
        this.cargarRespuestaPregunta();
      }
    },
    async siguiente() {
      const respuesta = this.respuestas[this.evaluacion.pregunta.id];
      const tieneRespuesta = respuesta !== undefined && respuesta !== null && respuesta !== '';
      const body = {
        respuestas: [respuesta],
      };
      if (!this.evaluacion.es_ultima) {
        this.loading = true;
        if (tieneRespuesta) {
          await evaluacionContratacion.guardarRespuesta(this.evaluacion.uuid_evaluacion_respuesta, this.evaluacion.pregunta.id, body);
        }
        const response = await evaluacionContratacion.getObtenerPregunta(this.evaluacion.uuid_evaluacion_respuesta, this.evaluacion.pregunta_siguiente);
        this.evaluacion = response.data.data;
        this.cargarRespuestaPregunta();
        this.loading = false;
      } else {
        this.loading = true
        if (tieneRespuesta) {
          await evaluacionContratacion.guardarRespuesta(this.evaluacion.uuid_evaluacion_respuesta, this.evaluacion.pregunta.id, body);
        }
        let responseResumen = await evaluacionContratacion.getResumen(this.evaluacion.uuid_evaluacion_respuesta)
        this.resumen = responseResumen.data.data
        this.idOferta = responseResumen.data.data.id_oferta
        this.mostrarRevision = true;
        this.loading = false
      }
    },
    async omitir() {
      if(!this.evaluacion.es_ultima) {
        const response = await evaluacionContratacion.getObtenerPregunta(this.evaluacion.uuid_evaluacion_respuesta, this.evaluacion.pregunta_siguiente)
        this.evaluacion = response.data.data
        this.cargarRespuestaPregunta();
      } else {
        this.loading = true
        let responseResumen = await evaluacionContratacion.getResumen(this.evaluacion.uuid_evaluacion_respuesta)
        this.resumen = responseResumen.data.data
        this.idOferta = responseResumen.data.data.id_oferta
        this.mostrarRevision = true;
        this.loading = false
      }
    },
    async irAPregunta(numero) {
      this.loading = true
      const response = await evaluacionContratacion.getObtenerPregunta(this.evaluacion.uuid_evaluacion_respuesta, numero)
      this.evaluacion = response.data.data
      this.cargarRespuestaPregunta();
      this.mostrarRevision = false;
      this.loading = false
    },
    async finalizar() {
      this.loading = true
      const response = await evaluacionContratacion.endExam(this.evaluacion.uuid_evaluacion_respuesta)
      this.temporalAlert({
        show: true,
        type: 'success',
        message: response.data.message
      })
      this.loading = false
      this.modalEvaluacionFinalizada = true;
    },
    openModalFinalizar() {
      this.modalFinalizar = true;
    },
    async confirmarFinalizar() {
      await this.finalizar();
      this.modalFinalizar = false;
    },
    aceptarFinalizacion() {
      this.modalEvaluacionFinalizada = false;
      this.$router.push({ name: 'seguimiento-postulacion', params : { id: this.idOferta } })
    },
    iniciarTimer() {
      this.timerInterval = setInterval(() => {
        if (this.segundosRestantes > 0) {
          this.segundosRestantes--;
        } else {
          clearInterval(this.timerInterval);
          this.finalizar();
        }
      }, 1000);
    },
  },
  async mounted() {
    await this.getEvaluacion();
  },
  beforeDestroy() {
    clearInterval(this.timerInterval);
  },
};
</script>

<style scoped>
.timer-badge {
  border: 1px solid #cf7a7c;
  background-color: #ffdad6;
  color: #cf7a7c;
  border-radius: 8px;
  padding: 8px 16px;
}

.help-cards-color {
  background-color: #f8f8f8;
}

.text-subtitle-2 {
  font-family: 'MuseoSans' !important;
  font-size: medium;
}

.revision-divider {
  border-width: 2px;
  opacity: 1;
  border-color: #1c1e4d;
}

.revision-card {
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}

.revision-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.pregunta-pendiente {
  border-style: dashed !important;
}
.opcion-card {
  cursor: pointer;
  border-color: #e0e0e0 !important;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.opcion-card:hover {
  border-color: #1c1e4d !important;
}

.opcion-card--selected {
  background-color: #eaeaea !important;
  border-color: #1c1e4d !important;
  color: #1c1e4d !important;
}

.opcion-badge {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 30%;
  border: 1px solid #b0b3c1;
  color: #1c1e4d;
  font-size: 13px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.opcion-badge--selected {
  background-color: #1c1e4d;
  border-color: #1c1e4d;
  color: white;
}
</style>
